/**
 * Navigation Component
 * Main navigation bar with routing, user status, and mobile menu
 */

import { authUI, authService } from '../../auth';

export type Route = '/' | '/online' | '/leaderboard' | '/friends' | '/history' | '/settings';

export interface NavigationConfig {
  onRouteChange: (route: Route) => void;
  onSettingsClick?: () => void;
}

export class Navigation {
  private container: HTMLElement;
  private config: NavigationConfig;
  private currentRoute: Route = '/';
  private mobileMenuOpen = false;

  constructor(container: HTMLElement, config: NavigationConfig) {
    this.container = container;
    this.config = config;
    this.render();
    this.setupHashListener();
  }

  /**
   * Get current route from hash
   */
  static getRouteFromHash(): Route {
    const hash = window.location.hash.slice(1) || '/';
    const validRoutes: Route[] = ['/', '/online', '/leaderboard', '/friends', '/history', '/settings'];
    return validRoutes.includes(hash as Route) ? (hash as Route) : '/';
  }

  /**
   * Navigate to a route
   */
  static navigateTo(route: Route): void {
    window.location.hash = route;
  }

  /**
   * Setup hash change listener
   */
  private setupHashListener(): void {
    const handleHashChange = () => {
      const newRoute = Navigation.getRouteFromHash();
      if (newRoute !== this.currentRoute) {
        this.currentRoute = newRoute;
        this.render();
        this.config.onRouteChange(newRoute);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    
    // Initial route
    this.currentRoute = Navigation.getRouteFromHash();
    this.config.onRouteChange(this.currentRoute);
  }

  /**
   * Get navigation items
   */
  private getNavItems(): Array<{ route: Route; label: string; icon: string; requiresAuth?: boolean }> {
    return [
      { route: '/', label: 'Home', icon: this.getHomeIcon() },
      { route: '/online', label: 'Play Online', icon: this.getOnlineIcon(), requiresAuth: true },
      { route: '/leaderboard', label: 'Leaderboard', icon: this.getLeaderboardIcon() },
      { route: '/friends', label: 'Friends', icon: this.getFriendsIcon(), requiresAuth: true },
      { route: '/history', label: 'History', icon: this.getHistoryIcon(), requiresAuth: true },
    ];
  }

  /**
   * Icons
   */
  private getHomeIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>`;
  }

  private getOnlineIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>`;
  }

  private getLeaderboardIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M8 21h8m-4-4v4M6 4h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
      <path d="M6 11v4a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-4"/>
    </svg>`;
  }

  private getFriendsIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>`;
  }

  private getHistoryIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>`;
  }

  private getSettingsIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>`;
  }

  private getMenuIcon(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>`;
  }

  private getCloseIcon(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>`;
  }

  /**
   * Render the navigation
   */
  render(): void {
    const isLoggedIn = !!authService.getCurrentUser();
    const navItems = this.getNavItems();

    this.container.innerHTML = `
      <nav class="main-nav">
        <div class="nav-content">
          <a href="#/" class="nav-logo">
            <span class="logo-text">Ariadne's String</span>
          </a>

          <div class="nav-items ${this.mobileMenuOpen ? 'open' : ''}">
            ${navItems.map(item => {
              const isActive = this.currentRoute === item.route;
              const isAuthLoading = authService.isLoading();
              const isDisabled = item.requiresAuth && !isLoggedIn && !isAuthLoading;
              const isLoading = item.requiresAuth && isAuthLoading;
              
              return `
                <a href="#${item.route}" 
                   class="nav-item ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''} ${isLoading ? 'loading' : ''}"
                   ${isDisabled ? 'title="Sign in required"' : ''}>
                  ${item.icon}
                  <span>${item.label}</span>
                </a>
              `;
            }).join('')}
          </div>

          <div class="nav-right">
            <button class="nav-settings-btn" title="Settings">
              ${this.getSettingsIcon()}
            </button>
            <div class="nav-user-status"></div>
            <button class="nav-mobile-toggle">
              ${this.mobileMenuOpen ? this.getCloseIcon() : this.getMenuIcon()}
            </button>
          </div>
        </div>
      </nav>

      <div class="mobile-menu-overlay ${this.mobileMenuOpen ? 'visible' : ''}"></div>
    `;

    // Add auth UI to user status area
    const userStatusContainer = this.container.querySelector('.nav-user-status');
    if (userStatusContainer) {
      const userStatusEl = authUI.createUserStatusElement();
      userStatusContainer.appendChild(userStatusEl);
    }

    this.attachEventListeners();
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    // Mobile menu toggle
    const mobileToggle = this.container.querySelector('.nav-mobile-toggle');
    mobileToggle?.addEventListener('click', () => {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      this.render();
    });

    // Mobile menu overlay
    const overlay = this.container.querySelector('.mobile-menu-overlay');
    overlay?.addEventListener('click', () => {
      this.mobileMenuOpen = false;
      this.render();
    });

    // Nav items click (close mobile menu)
    const navItems = this.container.querySelectorAll('.nav-item');
    navItems.forEach(item => {
      item.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLAnchorElement;
        if (target.classList.contains('disabled')) {
          e.preventDefault();
          authUI.showLoginModal();
          return;
        }
        this.mobileMenuOpen = false;
      });
    });

    // Settings button
    const settingsBtn = this.container.querySelector('.nav-settings-btn');
    settingsBtn?.addEventListener('click', () => {
      if (this.config.onSettingsClick) {
        this.config.onSettingsClick();
      } else {
        Navigation.navigateTo('/settings');
      }
    });
  }

  /**
   * Update current route highlight
   */
  setActiveRoute(route: Route): void {
    this.currentRoute = route;
    this.render();
  }

  /**
   * Destroy
   */
  destroy(): void {
    this.container.innerHTML = '';
  }
}

/**
 * Create navigation component
 */
export function createNavigation(container: HTMLElement, config: NavigationConfig): Navigation {
  return new Navigation(container, config);
}
